import { DroneIcon, SirenIcon, WarningIcon } from "@/assets/icons/Icons";
import { Button } from "@/components/ui/button";
import { useCircleSelection } from "@/context/CircleSelectionContext";
import { useIncidents } from "@/context/IncidentsContext";
import api from "@/services/api";
import { Incident } from "@/types/incidents";
import { formatRelativeTime } from "@/utils/formatTime";
import { formatUserInfo } from "@/utils/formatUserInfo";
import { MapPin, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Checkbox } from "../ui/checkbox";
import { LoadingSpinner } from "../ui/loading-spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const AlertsGroupingPanel = () => {
  const {
    selectedAlerts,
    setSelectedAlerts,
    setIsGroupingPanelOpen,
    setCircleSelectionMode,
    setCircleCenter,
    setCircleRadius,
  } = useCircleSelection();
  const { fetchIncidents } = useIncidents();
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [selectedAlertIds, setSelectedAlertIds] = useState<Set<number>>(
    new Set()
  );
  const location = useLocation();

  const handleClose = useCallback(() => {
    setIsGroupingPanelOpen(false);
    setCircleSelectionMode(false);
    setCircleCenter(null);
    setCircleRadius(0);
    setSelectedAlerts([]);
    setSelectedAlertIds(new Set());
  }, [setIsGroupingPanelOpen, setCircleSelectionMode, setCircleCenter, setCircleRadius, setSelectedAlerts]);

  useEffect(() => {
    const isAlertsPage = location.pathname === "/alerts";

    if (!isAlertsPage) {
      handleClose();
    }
  }, [location.pathname, handleClose]);

  useEffect(() => {
    const groupedAlertIds = new Set(
      selectedAlerts
        .filter((alert) => alert.grouped)
        .map((alert) => alert.incidentID)
    );
    setSelectedAlertIds(groupedAlertIds);
  }, [selectedAlerts]);

  const handleAlertToggle = (alert: Incident) => {
    const newSelectedIds = new Set(selectedAlertIds);
    if (newSelectedIds.has(alert.incidentID)) {
      newSelectedIds.delete(alert.incidentID);
    } else {
      newSelectedIds.add(alert.incidentID);
    }
    setSelectedAlertIds(newSelectedIds);
  };

  const handleCreateGroup = async () => {
    try {
      setIsCreatingGroup(true);
      const selectedIds = Array.from(selectedAlertIds);

      // Find if any selected alert is already grouped
      const groupedAlert = selectedAlerts.find(
        (alert) =>
          alert.grouped &&
          alert.groupID &&
          selectedAlertIds.has(alert.incidentID)
      );
      const setOfAlerts = selectedAlerts.filter(incident => selectedAlertIds.has(incident.incidentID))
      const uniqueLocations = new Set(setOfAlerts.map(incident => incident.locationID));

      if (uniqueLocations.size > 1) {
        toast.error("Location must be the same for all selected alerts");
        return;
      }
      if (selectedAlertIds.size > 1) {
        if (groupedAlert?.groupID) {
          // If we found a grouped alert, add other alerts to its group
          await api.put(`/incidents/group/${groupedAlert.groupID}`, {
            incidentIDs: selectedIds,
          });
          toast.success("Group updated successfully");
          fetchIncidents();
          handleClose();
        } else {
          // If no grouped alerts found, create a new group
          await api.post("/incidents/group", {
            incidentIDs: selectedIds,
          });
          toast.success(`Group created successfully`);
        }
        fetchIncidents();
        handleClose();
      } else {
        toast.error("Incident must be greater than one");
      }
    } catch (error) {
      console.error("Error managing group:", error);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const isAlreadyGroup = Array.from(selectedAlertIds).some((id) =>
    selectedAlerts.find((alert) => alert.incidentID === id)?.grouped
  );

  const groupLabel = `${isAlreadyGroup ? "Add to" : "Create"} Group (${selectedAlertIds.size} selected)`;

  return (
    <div className="fixed top-[60px] md:top-[60px] lg:top-0 w-full sm:w-[440px] h-full bg-white dark:bg-black shadow-lg pl-0 ml-0 sm:pl-4 sm:-ml-4">
      <div className="h-[60px] md:h-[70px] sm:h-[80px] px-6 py-4 flex items-center justify-between border-b border-[#E3E3E3]">
        <div className="text-sub-heading dark:text-gray-200">Alerts in selected area</div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label="Close"
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-50 dark:hover:bg-dark rounded-full transition-colors duration-200"
                >
                  <X size={"24px"} className="text-[#959697]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Close</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="h-[calc(100%-140px)] overflow-y-auto">
        <div className="p-4 space-y-2">
          {selectedAlerts.map((alert) => {
            const getAlertConfig = () => {
              let color = "bg-[#FF3B30]";
              let icon = <SirenIcon />;

              if (alert.incidentType === "NEAR_DANGER") {
                color = "bg-[#FFCC00]";
                icon = <WarningIcon />;
              } else if (alert.incidentType === "DRONE_ALERT") {
                color = "bg-[#26292C]";
                icon = <DroneIcon />;
              }

              return { color, icon };
            };

            const getSituationText = () => {
              if (alert.situation) return alert.situation;

              switch (alert.incidentType) {
                case "NEAR_DANGER":
                  return "Near Danger";
                case "DANGER":
                  return "Danger";
                case "DRONE_ALERT":
                  return "Drone Alert";
                default:
                  return alert.incidentType;
              }
            };

            const config = getAlertConfig();
            const situationText = getSituationText();

            return (
              <div
                key={alert.incidentID}
                className={`w-full sm:w-[388px] pt-4 px-4 pb-2 border-[1px] rounded-[8px] 
                hover:bg-gray-50 dark:hover:bg-dark transition-all duration-300 border-[#E3E3E3] relative`}
              >
                <div className="flex items-center gap-4 relative z-10 pointer-events-none">
                  <div
                    className={`w-[40px] h-[40px] rounded-full flex items-center justify-center 
                    ${config.color} text-white pointer-events-auto`}
                  >
                    {config.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-body-bold truncate dark:text-gray-200 pointer-events-auto">
                        Case ID #{alert.incidentID}
                      </div>
                      <div className="flex items-center text-[#05539c] pointer-events-auto">
                        <MapPin size={18} color="#05539c" />{alert?.locationID}
                      </div>
                      <Checkbox
                        className="z-10 pointer-events-auto"
                        checked={selectedAlertIds.has(alert.incidentID)}
                        onCheckedChange={() => handleAlertToggle(alert)}
                      />
                    </div>
                    <div className="flex items-center min-w-0">
                      <div className="text-overline-bold truncate overflow-hidden text-ellipsis whitespace-nowrap min-w-0 dark:text-gray-200">
                        {formatUserInfo(alert.ReportedByUser)} |
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-overline truncate ml-2 dark:text-gray-200">
                              {situationText}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs dark:text-gray-200">
                              {alert.situation || alert.incidentType}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
                <button
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onClick={() => handleAlertToggle(alert)}
                  aria-label={`Toggle selection for Case ID ${alert.incidentID}`}
                />
                <div className="flex items-center pt-2 justify-end gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-xs text-gray-600 italic dark:text-gray-200">
                          Reported {formatRelativeTime(alert.reportedOn)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">
                          {new Date(alert.reportedOn).toLocaleString()}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute lg:bottom-0 md:bottom-[6vh] w-[96.3%] p-4 border-t bg-white dark:bg-black">
        <Button
          onClick={handleCreateGroup}
          disabled={selectedAlertIds.size === 0 || isCreatingGroup}
          className="w-full btn-primary dark:bg-primary dark:text-gray-200 dark:hover:bg-dark"
        >
          {isCreatingGroup ? (
            <LoadingSpinner size="small" color="white" />
          ) : (
            groupLabel
          )}
        </Button>
      </div>
    </div>
  );
};

export default AlertsGroupingPanel;
